import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { messages } from './messages'
import { DATE_LOCALE, DEFAULT_LANG, isLang, type Lang } from './config'
import { useHousehold } from '@/context/HouseholdContext'

export type TFn = (key: string, params?: Record<string, string | number>) => string

interface I18nValue {
  lang: Lang
  dateLocale: string
  t: TFn
}

const I18nContext = createContext<I18nValue>({
  lang: DEFAULT_LANG,
  dateLocale: DATE_LOCALE[DEFAULT_LANG],
  t: (k) => k,
})

// Idioma vem do perfil (households.language). Fica DENTRO do HouseholdProvider.
// t(key, params): busca no idioma atual → fallback pt-BR → a própria chave.
// Interpolação simples: t('x.hello', { name }) em "Olá, {name}".
export function I18nProvider({ children }: { children: ReactNode }) {
  const { language } = useHousehold()
  const lang: Lang = isLang(language) ? language : DEFAULT_LANG

  const value = useMemo<I18nValue>(() => {
    const dict = messages[lang] ?? {}
    const fb = messages[DEFAULT_LANG] ?? {}
    const t: TFn = (key, params) => {
      let s = dict[key] ?? fb[key] ?? key
      if (params) for (const [k, v] of Object.entries(params)) s = s.split(`{${k}}`).join(String(v))
      return s
    }
    return { lang, dateLocale: DATE_LOCALE[lang], t }
  }, [lang])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useT() {
  return useContext(I18nContext)
}
